import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useCallback, useMemo} from 'react'

import type {PinControls} from '../components/views/PinViewMenuItem'
import type {SavedView} from '../components/views/types'
import {LABELS} from '../constants/labels'
import type {SavedViewMutationOptions} from '../utils/saved-view-mutations'
import {useSetSavedViewPinnedMutation} from './use-set-saved-view-pinned-mutation'

export interface SavedViewPinControlsResult {
  pinControls?: PinControls
  pinErrorMessage: string | null
  retryPin: () => void
  isPinRetrying: boolean
}

export function useSavedViewPinControls({owner, repo}: SavedViewMutationOptions): SavedViewPinControlsResult {
  const {mutate, isPending, error, variables} = useSetSavedViewPinnedMutation({owner, repo})
  const pinEnabled = isFeatureEnabled('issue_pinned_views')

  const togglePin = useCallback(
    (view: SavedView) => {
      mutate({id: view.id, pinned: !view.pinned})
    },
    [mutate],
  )

  const retryPin = useCallback(() => {
    if (variables) mutate(variables)
  }, [mutate, variables])

  const pinControls = useMemo<PinControls | undefined>(
    () => (pinEnabled ? {togglePin} : undefined),
    [pinEnabled, togglePin],
  )

  const pinErrorMessage =
    error && variables ? (variables.pinned ? LABELS.views.pinError : LABELS.views.unpinError) : null

  return {pinControls, pinErrorMessage, retryPin, isPinRetrying: isPending}
}
